import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useFlexLayout, useTable } from 'react-table';
import classnames from 'classnames';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../../store/global-selectors';
import FormattedCurrencyContainer from '../../../../components/formatted-currency/container';
import { doubleDash } from '../../constants';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const STORE = 'store';
const REVENUE = 'revenue';

const dataSourcesToAlign = [REVENUE];

const messages = defineMessages({
  title: {
    defaultMessage: 'Sales by Store',
    id: 'lDkBhU',
    description: 'sales by store table header',
  },
  store: {
    defaultMessage: 'Store',
    id: 'oJOl8+',
    description: 'sales by store table store header',
  },
  revenue: {
    defaultMessage: 'Revenue',
    id: 'gBkiIq',
    description: 'sales by store table revenue of store header',
  },
  addSellingChannels: {
    defaultMessage: "You haven't added any stores yet. <link>Add selling channels</link>",
    id: 'v1z2wl',
    description: 'message that is displayed in the sales by store table when a seller has no stores',
  },
});

const NUMBER_OF_STORES_CUTOFF = 4;

type StoreData = {
  name: string;
  storeGuid: string;
  marketplaceName?: string;
  count?: number;
  amount: number;
};

type SalesByStoreTableProps = {
  data?: StoreData[];
};

export const SalesByStoreTable = ({ data = emptyArray as StoreData[] }: SalesByStoreTableProps) => {
  const intl = useIntl();
  const sellerStores = useSelector(Global.selectSellerStores);
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const noStoresMessage = useMemo(
    () =>
      sellerStores.length ? undefined : (
        <FormattedMessage
          {...messages.addSellingChannels}
          values={{
            link: (chunks: React.ReactNode) => <Link to='/settings/stores'>{chunks}</Link>,
          }}
        />
      ),
    [sellerStores],
  );

  const renderAlignedCell = useCallback(
    (value: React.ReactNode, accessor: string) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {value}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{value}</div>
      ) : (
        value
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const renderAlignedHeader = useCallback(
    (accessor: string, title: React.ReactNode) => {
      if (dataSourcesToAlign.includes(accessor)) {
        return (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {title}
          </div>
        );
      }
      return isShipStationAnalyticsFeatureEnabled ? (
        <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{title}</div>
      ) : (
        title
      );
    },
    [isShipStationAnalyticsFeatureEnabled],
  );

  const columns = useMemo(
    () => [
      {
        Header: () => renderAlignedHeader(STORE, intl.formatMessage(messages[STORE])),
        accessor: STORE as typeof STORE,
        Cell: ({ value }: { value: string }) => renderAlignedCell(value, STORE),
      },
      {
        Header: () => renderAlignedHeader(REVENUE, intl.formatMessage(messages[REVENUE])),
        accessor: REVENUE as typeof REVENUE,
        Cell: ({ value }: { value: React.ReactNode }) => renderAlignedCell(value, REVENUE),
      },
    ],
    [intl, renderAlignedCell, renderAlignedHeader],
  );

  const tableData = useMemo(() => {
    if (!data.length && sellerStores.length) {
      return sellerStores.slice(0, NUMBER_OF_STORES_CUTOFF).map((store: { storeId: number; name: string }) => ({
        store: store.name,
        revenue: doubleDash,
      }));
    }
    return data.map((store) => ({
      store: store.name,
      revenue: <FormattedCurrencyContainer value={store.amount} />,
    }));
  }, [data, sellerStores]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames({
          [style['table-title']]: !isShipStationAnalyticsFeatureEnabled,
          [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled,
        })}
      >
        <Text textType={TextType.H3} value={messages.title} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        emptyRow={noStoresMessage}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};

export default SalesByStoreTable;
